import { useEffect, useState } from "react";
import { usePromptsStore } from "./promptsStore";
import CreatePrompt from "./CreatePrompt";
import SuggestPromptsModal from "./SuggestPromptsModal";
import {
  ActionIcon,
  Button,
  Center,
  Checkbox,
  Group,
  Modal,
  Pagination,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconEdit,
  IconLock,
  IconBulb,
  IconLanguage,
  IconPlus,
  IconSearch,
  IconSelector,
  IconTrash,
} from "@tabler/icons-react";
import { localizePrompts, updatePromptActive } from "./promptsApi";
import { notifications } from "@mantine/notifications";
import { useStore } from "../store";
import { useBrandContext } from "../Brands/brandContext";
import { supportedLanguages } from "../_Shared/LanguageSelector";
import QueryTypeBadge from "../_Shared/QueryTypeBadge";
import { queryTypeMeta, queryTypeOptions } from "../_Shared/queryTypes";
import PromptSourceIcon from "./PromptSourceIcon";
import { formatMarket } from "../_Shared/targetMarkets";

// Create CSS classes for styling (similar to ProductList.module.css)
const classes = {
  th: {
    padding: "0 !important",
  },
  control: {
    width: "100%",
    padding: "8px 12px",
    "&:hover": {
      backgroundColor: "var(--mantine-color-gray-0)",
    },
  },
  icon: {
    width: 21,
    height: 21,
    borderRadius: 21,
  },
  rowSelected: {
    backgroundColor: "var(--mantine-color-blue-0)",
  },
};

function Th({ children, reversed, sorted, onSort, ta, w }) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;
  return (
    <Table.Th ta={ta} w={w} style={classes.th}>
      <UnstyledButton onClick={onSort} style={classes.control}>
        <Group gap={4} justify="space-between" wrap="nowrap">
          <Text fw={500} fz="sm">
            {children}
          </Text>
          <Center style={classes.icon}>
            <Icon size={16} stroke={1.5} />
          </Center>
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
}

function filterData(data, search, typeFilter, industryFilter, aspectFilter) {
  let filtered = data;

  // Text search filter
  if (search) {
    const query = search.toLowerCase().trim();
    filtered = filtered.filter((item) => {
      return (
        item.displayName?.toLowerCase().includes(query) ||
        item.prompt?.toLowerCase().includes(query) ||
        item.queryType?.toLowerCase().includes(query) ||
        item.industry?.toLowerCase().includes(query) ||
        item.aspect?.toLowerCase().includes(query)
      );
    });
  }

  // Type filter
  if (typeFilter) {
    filtered = filtered.filter((item) => item.queryType === typeFilter);
  }

  // Industry filter
  if (industryFilter) {
    filtered = filtered.filter((item) => item.industry === industryFilter);
  }

  // Aspect filter
  if (aspectFilter) {
    filtered = filtered.filter((item) => item.aspect === aspectFilter);
  }

  return filtered;
}

function sortData(data, payload) {
  const { sortBy, filter, typeFilter, industryFilter, aspectFilter } = payload;

  if (!sortBy) {
    return filterData(data, filter, typeFilter, industryFilter, aspectFilter);
  }

  return filterData(
    [...data].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle special cases
      if (sortBy === "isActive") {
        aValue = a.isActive ? 1 : 0;
        bValue = b.isActive ? 1 : 0;
      }

      // Convert to string for comparison
      aValue = (aValue || "").toString();
      bValue = (bValue || "").toString();

      if (payload.reversed) {
        return bValue.localeCompare(aValue);
      }
      return aValue.localeCompare(bValue);
    }),
    filter,
    typeFilter,
    industryFilter,
    aspectFilter
  );
}

export default function PromptsList({ params }) {
  const { prompts, fetchPrompts, isLoading, deletePrompt } = usePromptsStore();
  const [createModalOpened, setCreateModalOpened] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [suggestModalOpened, setSuggestModalOpened] = useState(false);

  // New state for filtering, sorting, and pagination
  const [filter, setFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [aspectFilter, setAspectFilter] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [sortedData, setSortedData] = useState([]);
  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [selection, setSelection] = useState([]);

  const tenantProfile = useStore((state) => state.tenantProfile);
  const { selectedBrand } = useBrandContext();
  const [localizing, setLocalizing] = useState(false);

  // The market's language, and how many prompts are not written in it. A prompt is asked exactly
  // as stored — nothing translates it at fetch time — so this gap is the difference between
  // measuring the French market and measuring the English-speaking part of it.
  const marketLanguage = selectedBrand?.defaultTargetLanguageCode;
  const marketLanguageLabel =
    supportedLanguages.find((l) => l.value === marketLanguage)?.label?.replace(/\s*\(\w+\)$/, "") ??
    marketLanguage;
  const untranslated = prompts.filter(
    (p) => p.prompt && p.promptLanguageCode !== marketLanguage,
  ).length;
  const needsLocalization =
    !!marketLanguage && marketLanguage !== "en" && untranslated > 0;

  const handleLocalize = async () => {
    setLocalizing(true);
    try {
      const result = await localizePrompts(params.brandId);
      notifications.show({
        color: result?.queued ? "blue" : "gray",
        title: result?.queued ? "Rewriting your prompts" : "Nothing to rewrite",
        message: result?.queued
          ? `${result.pending} prompt(s) are being rewritten in ${marketLanguageLabel}. ` +
            "The English wording is kept beside each one. Refresh in a minute."
          : "Every prompt is already written in the market's language.",
      });
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Could not rewrite the prompts",
        message: error?.message ?? "Please try again.",
      });
    } finally {
      setLocalizing(false);
    }
  };

  const enableCreateButton =
    tenantProfile.plan === "Starter" ||
    tenantProfile.plan === "Growth" ||
    tenantProfile.plan === "Business" ||
    tenantProfile.plan === "Enterprise" ||
    tenantProfile.plan === "SUPER_ADMIN";
  const atWatchlistCap =
    tenantProfile?.packaging?.promptsLimit > 0 &&
    (tenantProfile.packaging.promptsActive ?? 0) >=
      tenantProfile.packaging.promptsLimit;

  const pageSize = 20; // Smaller page size for prompts

  useEffect(() => {
    fetchPrompts(params.brandId);
  }, [params.brandId]);

  // Update sorted data when prompts change
  useEffect(() => {
    if (prompts) {
      setSortedData(
        sortData(prompts, {
          sortBy,
          reversed: reverseSortDirection,
          filter,
          typeFilter,
          industryFilter,
          aspectFilter,
        })
      );
    }
  }, [
    prompts,
    sortBy,
    reverseSortDirection,
    filter,
    typeFilter,
    industryFilter,
    aspectFilter,
  ]);

  const toggleRow = (id) =>
    setSelection((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );

  const toggleAll = () =>
    setSelection((current) =>
      current.length === currentPageData.length
        ? []
        : currentPageData.map((item) => item.id)
    );

  const setSorting = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(
      sortData(prompts, {
        sortBy: field,
        reversed,
        filter,
        typeFilter,
        industryFilter,
        aspectFilter,
      })
    );
  };

  const handleSearchChange = (event) => {
    const { value } = event.currentTarget;
    setFilter(value);
    setActivePage(1); // Reset to first page when filtering
    setSortedData(
      sortData(prompts, {
        sortBy,
        reversed: reverseSortDirection,
        filter: value,
        typeFilter,
        industryFilter,
        aspectFilter,
      })
    );
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = {
      filter,
      typeFilter,
      industryFilter,
      aspectFilter,
    };

    switch (filterType) {
      case "type":
        newFilters.typeFilter = value;
        setTypeFilter(value);
        break;
      case "industry":
        newFilters.industryFilter = value;
        setIndustryFilter(value);
        break;
      case "aspect":
        newFilters.aspectFilter = value;
        setAspectFilter(value);
        break;
    }

    setActivePage(1); // Reset to first page when filtering
    setSortedData(
      sortData(prompts, {
        sortBy,
        reversed: reverseSortDirection,
        ...newFilters,
      })
    );
  };

  // Get unique values for filter dropdowns
  const uniqueTypes = [
    ...new Set(prompts?.map((p) => p.queryType).filter(Boolean)),
  ];
  const uniqueIndustries = [
    ...new Set(prompts?.map((p) => p.industry).filter(Boolean)),
  ];
  const uniqueAspects = [
    ...new Set(prompts?.map((p) => p.aspect).filter(Boolean)),
  ];

  // Bulk actions
  const enableSelected = async () => {
    const promises = selection.map((id) => {
      const prompt = prompts.find((p) => p.id === id);
      if (prompt && !prompt.isActive) {
        return updatePromptActive(id, true);
      }
      return Promise.resolve();
    });
    await Promise.all(promises);
    fetchPrompts(params.brandId); // Refresh data
  };

  const disableSelected = async () => {
    const promises = selection.map((id) => {
      const prompt = prompts.find((p) => p.id === id);
      if (prompt && prompt.isActive) {
        return updatePromptActive(id, false);
      }
      return Promise.resolve();
    });
    await Promise.all(promises);
    fetchPrompts(params.brandId); // Refresh data
  };

  // Calculate pagination
  const totalItems = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIndex = (activePage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageData = sortedData.slice(startIndex, endIndex);

  let rows = currentPageData.map((prompt) => {
    const selected = selection.includes(prompt.id);
    return (
      <Table.Tr
        style={selected ? classes.rowSelected : undefined}
        key={prompt.id}
      >
        <Table.Td>
          <Checkbox
            onClick={(e) => e.stopPropagation()}
            checked={selection.includes(prompt.id)}
            onChange={() => toggleRow(prompt.id)}
          />
        </Table.Td>
        <Table.Td>
          <SegmentedControl
            size="xs"
            withItemsBorders={false}
            value={prompt.status}
            onChange={async (value) => {
              const previous = prompt.status;
              setSortedData((current) =>
                current.map((p) =>
                  p.id === prompt.id ? { ...p, status: value } : p
                )
              );
              try {
                await updatePromptActive(prompt.id, value);
              } catch (error) {
                setSortedData((current) =>
                  current.map((p) =>
                    p.id === prompt.id ? { ...p, status: previous } : p
                  )
                );
                notifications.show({
                  color: "red",
                  title: "Could not change status",
                  message:
                    error?.message ??
                    "Turn another prompt off first, or move to Company.",
                });
              }
            }}
            color={
              prompt.status === "auto"
                ? "gray"
                : prompt.status === "on"
                ? "green"
                : "red"
            }
            data={[
              { label: "Auto", value: "auto" },
              { label: "On", value: "on" },
              { label: "Off", value: "off" },
            ]}
          />
        </Table.Td>
        <Table.Td>
          <PromptSourceIcon prompt={prompt} />
        </Table.Td>
        <Table.Td>
          <Text fz="sm">{prompt.prompt ?? prompt.id}</Text>
          {/* The reference, never the probe. A French market is measured by asking in French;
              this line is so the person running the brand can tell the prompts apart. */}
          {prompt.promptInEnglish && (
            <Text fz="xs" c="dimmed" mt={2}>
              {prompt.promptInEnglish}
            </Text>
          )}
        </Table.Td>
        <Table.Td>
          <QueryTypeBadge queryType={prompt.queryType} />
        </Table.Td>
        <Table.Td>{prompt.industry}</Table.Td>
        <Table.Td>
          {prompt.defaultTargetCountryCode || prompt.defaultTargetLanguageCode
            ? formatMarket(
                prompt.defaultTargetCountryCode,
                prompt.defaultTargetLanguageCode
              )
            : "Home"}
        </Table.Td>
        <Table.Td>{prompt.aspect}</Table.Td>
        <Table.Td>
          <Group wrap="nowrap" gap={4}>
            <ActionIcon
              variant="subtle"
              onClick={() => {
                setItemToEdit(prompt);
                setCreateModalOpened(true);
              }}
            >
              <IconEdit size={16} />
            </ActionIcon>
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={() => {
                setPromptToDelete(prompt);
                setDeleteModalOpened(true);
              }}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </Table.Td>
      </Table.Tr>
    );
  });

  if (rows.length === 0 && isLoading) {
    rows = (
      <Table.Tr>
        <Table.Td colSpan={9}>
          <Stack align="center" justify="center" mih={80}>
            <Text ta="center" c="dimmed">
              Loading...
            </Text>
          </Stack>
        </Table.Td>
      </Table.Tr>
    );
  }

  if (rows.length === 0 && !isLoading) {
    rows = (
      <Table.Tr>
        <Table.Td colSpan={9}>
          <Text ta="center" c="dimmed">
            {filter ? "No prompts match the filter" : "No prompts found"}
          </Text>
        </Table.Td>
      </Table.Tr>
    );
  }

  // No Container of its own. It sits inside the page's Container already, and a nested one re-applied
  // the theme's 800px default — clamping the table back to half the window no matter how wide the
  // page was made.
  return (
    <>
      <Stack gap="xs">
        <Group mt="sm" justify="space-between">
          <TextInput
            placeholder="Search prompts..."
            leftSection={<IconSearch size={16} stroke={1.5} />}
            value={filter}
            onChange={handleSearchChange}
          />

          <Group gap="xs">
            {needsLocalization && (
              <Tooltip
                label={
                  `${untranslated} prompt(s) are not written in ${marketLanguageLabel}. ` +
                  "They are asked exactly as written, so they measure the English-language " +
                  "market wherever the search is run from."
                }
                multiline
                w={320}
              >
                <Button
                  variant="light"
                  color="warning"
                  leftSection={<IconLanguage size={16} />}
                  loading={localizing}
                  onClick={handleLocalize}
                >
                  Ask these in {marketLanguageLabel}
                </Button>
              </Tooltip>
            )}
            <Button
              variant="light"
              leftSection={<IconBulb size={16} />}
              onClick={() => setSuggestModalOpened(true)}
              disabled={
                !["Growth", "Business", "Enterprise", "SUPER_ADMIN"].includes(
                  tenantProfile.plan
                )
              }
            >
              Suggest Prompts
            </Button>
            {tenantProfile?.packaging?.promptsLimit > 0 && (
              <Text size="sm" c="dimmed">
                {(tenantProfile.packaging.promptsActive ?? 0).toLocaleString()} of{" "}
                {tenantProfile.packaging.promptsLimit.toLocaleString()} questions
                active on this plan
              </Text>
            )}
            <Tooltip
              label={
                !enableCreateButton
                  ? "Upgrade to a paid plan to create custom prompts"
                  : atWatchlistCap
                    ? "Turn one question off before adding another, or move to Company"
                    : "Create a new prompt"
              }
            >
              <Button
                leftSection={
                  enableCreateButton ? (
                    <IconPlus size={16} />
                  ) : (
                    <IconLock size={16} />
                  )
                }
                disabled={!enableCreateButton}
                onClick={() => {
                  setItemToEdit(null);
                  setCreateModalOpened(true);
                }}
              >
                Create New Prompt
              </Button>
            </Tooltip>
          </Group>
        </Group>

        <Group mb="sm" align="flex-end" justify="space-between">
          <Group>
            <Pagination
              hideWithOnePage={false}
              total={totalPages}
              value={activePage}
              onChange={setActivePage}
            />

            {/* Was `data={uniqueTypes}`, which printed the wire values —
                "aspectSpecificUnaidedAwareness" — and truncated them at the input width, so the
                same type was named one way here and another in the badge beside it. Options carry
                the badge itself, so the colour coding in the table is the colour coding you filter
                by. */}
            <Select
              placeholder="Filter by Type"
              data={queryTypeOptions(uniqueTypes)}
              value={typeFilter}
              onChange={(value) => handleFilterChange("type", value)}
              renderOption={({ option }) => (
                <Group gap="xs" wrap="nowrap">
                  <QueryTypeBadge queryType={option.value} />
                  <Text size="sm">{queryTypeMeta(option.value).label}</Text>
                </Group>
              )}
              clearable
              w={220}
            />

            <Select
              placeholder="Filter by Industry"
              data={uniqueIndustries}
              value={industryFilter}
              onChange={(value) => handleFilterChange("industry", value)}
              clearable
              w={200}
            />

            <Select
              placeholder="Filter by Aspect"
              data={uniqueAspects}
              value={aspectFilter}
              onChange={(value) => handleFilterChange("aspect", value)}
              clearable
              w={200}
            />
          </Group>

          {selection.length > 0 && (
            <Group>
              <Text size="sm" c="dimmed">
                {selection.length} selected
              </Text>
              <Button.Group>
                <Button variant="default" size="sm" onClick={enableSelected}>
                  Enable
                </Button>
                <Button variant="default" size="sm" onClick={disableSelected}>
                  Disable
                </Button>
              </Button.Group>
            </Group>
          )}
        </Group>
      </Stack>

      <SuggestPromptsModal
        opened={suggestModalOpened}
        onClose={() => setSuggestModalOpened(false)}
        brandId={params.brandId}
      />
      <CreatePrompt
        modalOpened={createModalOpened}
        setModalOpened={setCreateModalOpened}
        brandId={params.brandId}
        initialValues={itemToEdit}
      />
      <Modal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        title="Delete Prompt?"
        centered
      >
        <Text mb="md">Are you sure you want to delete this prompt?</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setDeleteModalOpened(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={async () => {
              if (promptToDelete) {
                await deletePrompt(promptToDelete.id);
                setDeleteModalOpened(false);
                setPromptToDelete(null);
                // Optionally, re-fetch prompts for full sync (if needed)
                // await fetchPrompts(params.brandId);
              }
            }}
          >
            Delete
          </Button>
        </Group>
      </Modal>

      <Table striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Checkbox
                onChange={toggleAll}
                checked={
                  selection.length === currentPageData.length &&
                  currentPageData.length > 0
                }
                indeterminate={
                  selection.length > 0 &&
                  selection.length !== currentPageData.length
                }
              />
            </Table.Th>
            <Th
              sorted={sortBy === "status"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("status")}
            >
              Active
            </Th>
            <Th
              sorted={sortBy === "source"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("source")}
              w={56}
            >
              From
            </Th>
            <Th
              sorted={sortBy === "prompt"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("prompt")}
            >
              Prompt
            </Th>
            {/* Fixed width, and wide enough for the longest badge ("Reputation · aspect" is about
                145px at Badge size sm, uppercased) plus cell padding. Letting this column shrink to
                fit the prompt text is the other half of why the badges rendered as "AWARENESS…". */}
            <Th
              sorted={sortBy === "queryType"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("queryType")}
              w={200}
            >
              Type
            </Th>
            <Th
              sorted={sortBy === "industry"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("industry")}
            >
              Industry
            </Th>
            <Table.Th>Market</Table.Th>
            <Th
              sorted={sortBy === "aspect"}
              reversed={reverseSortDirection}
              onSort={() => setSorting("aspect")}
            >
              Aspect
            </Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>
    </>
  );
}
